import type { LayoutProps, ResponsiveValue, SpacingProps } from '@shopify/restyle';

import { Column } from './box';
import { Text } from './text';
import Slider from '@react-native-community/slider';
import { useState } from 'react';
import { type ColorValue } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import type { Theme } from 'app/shopify';
const theme = createDSTheme();

interface AdjustableProps extends LayoutProps<Theme>, SpacingProps<Theme> {
  disabled?: boolean;
  label: (value: number) => string;
  labelOnMax?: string;
  max: number;
  min: number;
  onChange: (value: number) => void;
  onSlidingComplete?: (value: number) => void;
  step: number;
  value: number;
  textColor?: ColorValue;
  backgroundColor?: ResponsiveValue<keyof Theme['colors'], Theme['breakpoints']>;
}

export function Adjustable({
  disabled,
  label,
  labelOnMax,
  max,
  min,
  onChange,
  onSlidingComplete,
  step,
  value,
  textColor = theme.colors['text.primary'],
  ...rest
}: Readonly<AdjustableProps>) {
  // Use the first 1/2 of the slider to work in the first 1/20 of the range
  const range = max - min;
  const percentThreshold = 1 / 2;
  const thresholdValue = Math.round(range / 20 / 1000) * 1000;
  const calculateNewValue = (sliderValue: number) => {
    let newValue = sliderValue;
    if (range > 100000) {
      const percentage = newValue / range;
      if (percentage < percentThreshold) {
        newValue = min + Math.round((thresholdValue * percentage) / percentThreshold / 1000) * 1000;
      } else {
        newValue =
          min +
          thresholdValue +
          Math.round(((range - thresholdValue) * (percentage - percentThreshold)) / (1 - percentThreshold) / 1000) *
            1000;
      }
      newValue = Math.min(newValue, max);
    }
    return newValue;
  };

  const calculateNewValueReverse = (targetValue: number) => {
    for (let i = min; i <= max; i += step) {
      if (calculateNewValue(i) >= targetValue) {
        return i;
      }
    }
    return max;
  };

  const [slidingValue, setSlidingValue] = useState<number | null>(null);
  const calcValue = slidingValue ?? calculateNewValueReverse(value);

  const sliderColors = [theme.colors['surface.tertiary'], theme.colors['surface.brand.default']];

  return (
    <Column
      {...rest}
      accessibilityRole="adjustable"
      accessibilityState={{ disabled }}
      accessibilityValue={{
        min,
        max,
        now: value,
      }}
      alignItems="center"
      borderRadius="radius.lg"
      gap="spacing.general.0"
      paddingHorizontal="spacing.general.x2"
    >
      <Text textAlign="center" variant="bodyBase" style={{ color: textColor }}>
        {value === max && labelOnMax ? labelOnMax : label(value)}
      </Text>

      <Slider
        disabled={disabled}
        maximumTrackTintColor={sliderColors[0]}
        maximumValue={max}
        minimumTrackTintColor={sliderColors[1]}
        minimumValue={min}
        onSlidingStart={setSlidingValue}
        onSlidingComplete={(newValue) => {
          onSlidingComplete?.(calculateNewValue(newValue));
          setSlidingValue(null);
        }}
        onValueChange={(newValue) => {
          if (slidingValue !== null) setSlidingValue(newValue);
          onChange(calculateNewValue(newValue));
        }}
        step={step}
        style={{ width: '100%', height: 40 }}
        thumbTintColor={sliderColors[1]}
        value={calcValue}
      />
    </Column>
  );
}
